import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { onServerEvent } from "@/lib/events";
import { toAppNotification, type AppNotification, type NotifyPrefs, type ServerNotification } from "./notifications-meta";

/* Уведомления пользователя с сервера: список, счётчик непрочитанных, прочтение, архив и возврат из архива.
   Новые приходят сразу по SSE; если в настройках включены браузерные — показываем системное окно, пока вкладка не на экране. */

export const PREFS_EVENT = "hub:notify-prefs";

export default function useNotifications() {
  const [items, setItems] = useState<AppNotification[]>([]);
  const browser = useRef(false);

  const load = useCallback(async () => {
    try {
      const r = await api<{ items: ServerNotification[] }>("/notifications");
      setItems(r.items.map((n) => toAppNotification(n)));
    } catch {
      /* без сети колокольчик просто пустой */
    }
  }, []);

  useEffect(() => {
    void load();
    api<NotifyPrefs>("/notifications/prefs").then((p) => (browser.current = p.channels.browser), () => undefined);
    const onPrefs = (e: Event) => (browser.current = Boolean((e as CustomEvent<NotifyPrefs>).detail?.channels?.browser));
    window.addEventListener(PREFS_EVENT, onPrefs);
    const offHello = onServerEvent("hello", () => void load());
    const offNew = onServerEvent("notification", (data) => {
      const n = toAppNotification(data as ServerNotification);
      setItems((prev) => (prev.some((x) => x.id === n.id) ? prev : [n, ...prev]));
      try {
        if (browser.current && document.hidden && typeof Notification !== "undefined" && Notification.permission === "granted") {
          new Notification(n.title, { body: n.text, tag: `hub-${n.id}` });
        }
      } catch {
        /* браузер без системных уведомлений */
      }
    });
    return () => {
      window.removeEventListener(PREFS_EVENT, onPrefs);
      offHello();
      offNew();
    };
  }, [load]);

  const unread = useMemo(() => items.filter((n) => !n.read && !n.archived).length, [items]);

  // меняем сразу на экране, сервер догоняет; при ошибке перечитываем список
  const act = useCallback(
    (id: string, path: string, patch: Partial<AppNotification>) => {
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, ...patch } : n)));
      api(`/notifications/${id}/${path}`, { method: "POST" }).catch(() => void load());
    },
    [load],
  );

  const read = useCallback((id: string) => act(id, "read", { read: true }), [act]);
  const archive = useCallback((id: string) => act(id, "archive", { archived: true, read: true }), [act]);
  const restore = useCallback((id: string) => act(id, "restore", { archived: false, read: true }), [act]);

  const archiveAll = useCallback(() => {
    setItems((prev) => prev.map((n) => (n.archived ? n : { ...n, archived: true, read: true })));
    api("/notifications/archive-all", { method: "POST" }).catch(() => void load());
  }, [load]);

  /** Тестовое уведомление себе: сервер создаёт его по вашим настройкам, на экран оно приходит по SSE */
  const sendTest = useCallback(async () => {
    const r = await api<{ created: boolean }>("/notifications/test", { method: "POST" });
    void load();
    return r.created;
  }, [load]);

  return { items, unread, read, archive, restore, archiveAll, sendTest, reload: load };
}
